/**
 * Hero Layout Preview Modal Component
 * 
 * Shows a preview of planned reorganization operations before execution.
 * Displays what will be reordered, created, and orphaned.
 * Allows users to confirm or cancel the reorganization.
 */

import React, { useEffect } from 'react';
import { Toggle } from '../../../design-system';
import { typography, surfaces, borders, textColors, interactive } from '../../../design-system/tokens';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Object} props.operations - Preview of planned operations
 * @param {Array} props.operations.reorders - Items to reorder
 * @param {Array} props.operations.creates - Items to create
 * @param {Array} props.operations.orphans - Items that will be orphaned
 * @param {Object} props.preset - The preset being applied
 * @param {Object} props.orphanDecisions - Map of item ID -> boolean (true = keep, false = delete)
 * @param {Function} props.onOrphanDecisionChange - Callback to update orphan decisions
 * @param {Function} props.onConfirm - Callback when user confirms
 * @param {Function} props.onCancel - Callback when user cancels
 */
export default function HeroLayoutPreviewModal({
  isOpen,
  operations,
  preset,
  orphanDecisions = {},
  onOrphanDecisionChange,
  onConfirm,
  onCancel
}) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const reorders = operations?.reorders || [];
  const creates = operations?.creates || [];
  const orphans = operations?.orphans || [];

  // Debug logging
  console.log('========================================');
  console.log('[PreviewModal] MODAL IS OPEN');
  console.log('[PreviewModal] orphanDecisions prop:', orphanDecisions);
  console.log('[PreviewModal] orphans:', orphans.map(o => `${o.type} (id: ${o.id})`));
  orphans.forEach(orphan => {
    const isKept = orphanDecisions[orphan.id] !== false;
    console.log(`[PreviewModal] Orphan ${orphan.type} (id: ${orphan.id}): decision=${orphanDecisions[orphan.id]}, isKept=${isKept}, will show as ${isKept ? 'KEEP (green)' : 'DELETE (red)'}`);
  });
  console.log('========================================');

  // Handler for individual checkbox toggle
  const handleOrphanToggle = (orphanId) => {
    const currentValue = orphanDecisions[orphanId] !== false; // Default to true
    const newValue = !currentValue;
    const newDecisions = {
      ...orphanDecisions,
      [orphanId]: newValue
    };
    console.log(`[PreviewModal] Toggling orphan ${orphanId}: ${currentValue} -> ${newValue}`);
    console.log('[PreviewModal] New decisions:', newDecisions);
    onOrphanDecisionChange?.(newDecisions);
  };

  // Handler for "Keep All" button
  const handleKeepAll = () => {
    const newDecisions = {};
    orphans.forEach(orphan => {
      newDecisions[orphan.id] = true;
    });
    onOrphanDecisionChange?.(newDecisions);
  };

  // Handler for "Delete All" button
  const handleDeleteAll = () => {
    const newDecisions = {};
    orphans.forEach(orphan => {
      newDecisions[orphan.id] = false;
    });
    onOrphanDecisionChange?.(newDecisions);
  };

  const totalChanges = reorders.length + creates.length + (orphans.length > 0 ? 1 : 0);

  // Type labels for better readability
  const typeLabels = {
    image: 'Image',
    title: 'Title',
    text: 'Text',
    button_collection: 'Button Collection',
    video: 'Video',
    file: 'File',
    button: 'Button'
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={() => onCancel?.()}
    >
      <div
        className={`${surfaces.elevated} rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${borders.default}`}>
          <h2 className={`font-heading text-xl font-bold ${textColors.primary}`}>
            Preview Layout Changes
          </h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
            Review the changes that will be applied to your hero layout
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Preset Info */}
          {preset && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className={`${typography.bodySmall} font-semibold text-blue-900 dark:text-blue-100 mb-1`}>
                Applying Preset: {preset.label}
              </h3>
              <p className={`${typography.bodySmall} text-blue-700 dark:text-blue-300`}>{preset.description}</p>
            </div>
          )}

          {/* Summary */}
          <div className="mb-6">
            <h3 className={`${typography.bodySmall} font-semibold ${textColors.primary} mb-3`}>Summary</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className={`${surfaces.secondary} rounded-lg p-3 text-center`}>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{reorders.length}</div>
                <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Items Reordered</div>
              </div>
              <div className={`${surfaces.secondary} rounded-lg p-3 text-center`}>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{creates.length}</div>
                <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Items Created</div>
              </div>
              <div className={`${surfaces.secondary} rounded-lg p-3 text-center`}>
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{orphans.length}</div>
                <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Items Orphaned</div>
              </div>
            </div>
          </div>

          {/* No Changes */}
          {totalChanges === 0 && (
            <div className={`p-4 ${surfaces.secondary} rounded-lg text-center ${textColors.secondary}`}>
              <svg className={`w-12 h-12 mx-auto mb-2 ${textColors.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className={`${typography.bodySmall} font-medium`}>No changes needed</p>
              <p className={`${typography.caption} mt-1`}>Your hero already matches this layout</p>
            </div>
          )}

          {/* Orphan Items Management - show this first */}
          {orphans.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className={`${typography.bodySmall} font-semibold ${textColors.primary} mb-1`}>
                    Extra Items ({orphans.length})
                  </h3>
                  <p className={`${typography.caption} ${textColors.secondary}`}>
                    These items don't fit in the new layout. Keep them (moved to end) or delete them.
                  </p>
                </div>
                {/* Shortcut Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleKeepAll}
                    className={`px-3 py-1.5 ${typography.caption} text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md border border-green-200 dark:border-green-800 transition-colors font-medium`}
                  >
                    Keep All
                  </button>
                  <button
                    onClick={handleDeleteAll}
                    className={`px-3 py-1.5 ${typography.caption} text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md border border-red-200 dark:border-red-800 transition-colors font-medium`}
                  >
                    Delete All
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                {orphans.map((item) => {
                  const isKept = orphanDecisions[item.id] !== false; // Default to true if not set
                  return (
                    <div 
                      key={item.id} 
                      className={`flex items-center justify-between p-3 ${surfaces.secondary} rounded-lg border ${borders.default} ${borders.focus} transition-colors`}
                    >
                      <span className={`${typography.bodySmall} font-medium ${textColors.primary}`}>
                        {typeLabels[item.type] || item.type}
                      </span>
                      
                      <div className="flex items-center gap-3">
                        <span className={`${typography.caption} font-medium ${
                          isKept ? 'text-blue-700 dark:text-blue-300' : 'text-red-700 dark:text-red-400'
                        }`}>
                          {isKept ? 'Keep' : 'Delete'}
                        </span>
                        
                        <Toggle
                          checked={isKept}
                          onChange={() => handleOrphanToggle(item.id)}
                          aria-label={`Toggle ${typeLabels[item.type] || item.type}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reorder Details */}
          {reorders.length > 0 && (
            <div className="mb-6">
              <h3 className={`${typography.bodySmall} font-semibold ${textColors.primary} mb-2 flex items-center`}>
                <svg className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
                </svg>
                Items to Reorder ({reorders.length})
              </h3>
              <div className="space-y-2">
                {reorders.slice(0, 5).map((op, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded ${typography.bodySmall}`}>
                    <span className={`font-medium ${textColors.primary}`}>
                      {typeLabels[op.itemType] || op.itemType}
                    </span>
                    <span className={textColors.secondary}>
                      Position {op.currentOrder + 1} → {op.newOrder + 1}
                    </span>
                  </div>
                ))}
                {reorders.length > 5 && (
                  <div className={`${typography.caption} ${textColors.muted} text-center`}>
                    ...and {reorders.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Details */}
          {creates.length > 0 && (
            <div className="mb-6">
              <h3 className={`${typography.bodySmall} font-semibold ${textColors.primary} mb-2 flex items-center`}>
                <svg className="w-4 h-4 mr-1.5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New Items to Create ({creates.length})
              </h3>
              <div className="space-y-2">
                {creates.slice(0, 5).map((op, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded ${typography.bodySmall}`}>
                    <span className={`font-medium ${textColors.primary}`}>
                      {typeLabels[op.type] || op.type}
                    </span>
                    <span className={textColors.secondary}>
                      At position {op.order + 1}
                    </span>
                  </div>
                ))}
                {creates.length > 5 && (
                  <div className={`${typography.caption} ${textColors.muted} text-center`}>
                    ...and {creates.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={`px-6 py-4 border-t ${borders.default} flex justify-end space-x-3`}>
          <button
            onClick={onCancel}
            className={`px-4 py-2 ${textColors.primary} ${surfaces.tertiary} ${surfaces.hover} rounded-md transition-colors ${typography.button}`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md transition-colors font-medium flex items-center ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
}

